import React, { useContext, useState } from "react";
import { useLocation } from "react-router-dom";

import { InfiniteTable } from "../../../../components";
import { SettingsContext, UserContext } from "../../../../contexts";
import FilterBy from "../../../../views/Orders/FilterBy";

import QuoteItem from "./QuoteItem";
import { useQuoteProductPricing } from "../../../hooks/quoteProductPricing";

const quoteTypeOptions = [
  { id: "order", name: "Order Quotes" },
  { id: "sell", name: "Sell Quotes" },
];

const clientRequestOptions = [
  { id: "client", name: "Client Quotes" },
  { id: "for_merchant", name: "Our Quotes" },
  { id: "by_merchant", name: "Merchant Quotes" },
  { id: "all", name: "View All" },
];

const clientMerchantRequestOptions = [
  { id: "for_merchant", name: "Our Quotes" },
  { id: "client", name: "Client Quotes" },
  { id: "by_merchant", name: "Merchant Quotes" },
  { id: "all", name: "View All" },
];

const merchantRequestOptions = (appSettings) => [
  { id: "by_merchant", name: "My Quotes" },
  { id: "all", name: "View All" },
  { id: "for_merchant", name: appSettings?.name_for_merchants },
];

const getQuoteSourceOptions = (context, settings) => {
  switch (context) {
    case "client":
      return clientRequestOptions;
    case "client_merchant":
      return clientMerchantRequestOptions;
    case "merchant":
      return merchantRequestOptions(settings);
    default:
      return [];
  }
};

const getContext = (user, location) => {
  if (user.is_merchant_admin) {
    return "merchant";
  }

  return location.pathname.indexOf("requests/merchant") > -1
    ? "client_merchant"
    : "client";
};

const QuoteListView = ({
  item,
  selectItem,
  refresh,
  chooseQuote,
  chosenQuote,
  onlyApproved,
  liveCalculation = false,
  type,
  setType,
  headerAction,
  // SELECT ORDER/SELL QUOTE only — see QuoteItem.
  rowActions = false,
  onRowQuoteSaved,
}) => {
  const requestId = item?.id ?? item?.request_id;
  const { settings } = useContext(SettingsContext);
  const { user } = useContext(UserContext);
  const location = useLocation();
  const context = getContext(user, location);
  const sourceOptions = getQuoteSourceOptions(context, settings);
  const defaultSource =
    sourceOptions.find((opt) => opt.id === "all")?.id ?? sourceOptions[0].id;
  const [source, setSource] = useState(defaultSource);
  
  const quoteType = type || "sell";
  const hasProductPricing = useQuoteProductPricing();

  // ACTION sits SECOND, beside the status it acts on. As the last of ten columns it was off
  // the right edge of the section and unreachable (Alex, 2026-09-05).
  const action = rowActions ? ["Action"] : [];
  const headers =
    quoteType === "order"
      ? [
          ...(chooseQuote ? [""] : []),
          "Status",
          ...action,
          "Created",
          "Admin Name",
          "Quoted Price",
          // Both read the PRODUCT PRICING row — hidden where the store has no
          // such dropdown (QuoteItem drops the matching cells).
          ...(hasProductPricing ? ["Supplier Name", "Buying Price"] : []),
          "Year",
          "Condition",
        ]
      : [
          ...(chooseQuote ? [""] : []),
          "Status",
          ...action,
          "Created",
          "Admin Name",
          "Quoted Price",
          "Buyer",
          "Consignment",
          "Year",
          "Condition",
        ];

  const onSelectClick = (quote) => {
    selectItem(quote.id, quote);
  };

  if (!item?.product_id) {
    return null;
  }

  return (
    <div className="quote-list-view">
      <div className="quote-list-toolbar">
        <div className="quote-list-toolbar__filters">
          <FilterBy
            {...{
              placeholder: "Filter by Request",
              options: quoteTypeOptions,
              filterBy: quoteType,
              setFilterBy: setType,
              className: "select-container quote-list-toolbar__select",
            }}
          />
          <FilterBy
            {...{
              placeholder: sourceOptions[0].name,
              options: sourceOptions,
              filterBy: source,
              setFilterBy: setSource,
              className: "select-container quote-list-toolbar__select",
            }}
          />
        </div>
        {headerAction && (
          <div className="quote-list-toolbar__actions">
            {headerAction}
          </div>
        )}
      </div>

      <InfiniteTable
        key={refresh}
        className="quotes-list"
        fetchUrl={`/api/quotes/list`}
        fetchLimit={6}
        firstPageOnly
        fetchParams={{
          filters: {
            product_id: item.product_id,
            request_id: requestId,
            type: quoteType,
            source,
          },
        }}
        emptyText="There are no quotes yet"
        headers={headers}
        onSelectClick={onSelectClick}
        RowComponent={QuoteItem}
        rowComponentProps={{
          chooseQuote,
          chosenQuote,
          onlyApproved,
          liveCalculation,
          requestId,
          rowActions,
          onRowQuoteSaved,
        }}
      />
    </div>
  );
};

export default QuoteListView;
